// API Configuration for DIOS10 Frontend
// This file centralizes all API endpoints and configuration

// Backend base URL - allow local fallback for dev
const rawApiBaseUrl = process.env.REACT_APP_BACKEND_URL ||
  (process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '');
const API_BASE_URL = rawApiBaseUrl ? rawApiBaseUrl.replace(/\/+$/, '') : '';

if (!rawApiBaseUrl) {
    // eslint-disable-next-line no-console
    console.warn('Missing REACT_APP_BACKEND_URL environment variable');
}

// API endpoints configuration
export const API_ENDPOINTS = {
    // Base API path
    BASE: `${API_BASE_URL}/api`,

    // Users endpoints
    USERS: {
        BASE: `${API_BASE_URL}/api/users`,
        REGISTER: `${API_BASE_URL}/api/users/register`,
        LOGIN: `${API_BASE_URL}/api/users/login`,
        GET_BY_ID: (id) => `${API_BASE_URL}/api/users/${id}`,
        GET_BY_ADDRESS: (address) => `${API_BASE_URL}/api/users/address/${address}`,
        GET_BY_USERNAME: (username) => `${API_BASE_URL}/api/users/username/${username}`,
        CHECK_AUTH: `${API_BASE_URL}/api/users/check-auth`,
        UPDATE_TUTORIAL_COMPLETED: (id) => `${API_BASE_URL}/api/users/${id}/tutorial-completed`,
        UPDATE_LANGUAGE: (id) => `${API_BASE_URL}/api/users/${id}/language`,
        UPDATE_PASSWORD: (id) => `${API_BASE_URL}/api/users/${id}/password`,
        UPDATE_GALLERY_PUBLIC: (id) => `${API_BASE_URL}/api/users/${id}/gallery-public`,
        UPDATE_WALLET: (id) => `${API_BASE_URL}/api/users/${id}/wallet`,
        UPDATE_SKRILL_EMAIL: (id) => `${API_BASE_URL}/api/users/${id}/skrill-email`,
        UPDATE_WISE_EMAIL: (id) => `${API_BASE_URL}/api/users/${id}/wise-email`,
        LOGIN_HISTORY: (id) => `${API_BASE_URL}/api/users/${id}/login-history`,
        SECURITY_REPORT: `${API_BASE_URL}/api/users/security-report`,
        SECURITY_REPORTS: (id) => `${API_BASE_URL}/api/users/${id}/security-reports`,
        PASSWORD_RESET_REQUEST: `${API_BASE_URL}/api/users/password-reset/request`,
        PASSWORD_RESET_RESET: `${API_BASE_URL}/api/users/password-reset/reset`,
        ANNOUNCEMENTS: `${API_BASE_URL}/api/users/announcements`,
        NOTIFICATIONS: (id) => `${API_BASE_URL}/api/users/${id}/notifications`,
        MARK_NOTIFICATION_READ: (id, notificationId) => `${API_BASE_URL}/api/users/${id}/notifications/${notificationId}/read`,
        MARK_ALL_NOTIFICATIONS_READ: (id) => `${API_BASE_URL}/api/users/${id}/notifications/read-all`,
        MAINTENANCE_ALERTS: `${API_BASE_URL}/api/users/maintenance-alerts`,
        ACTIVITY_REPORT: (id) => `${API_BASE_URL}/api/users/${id}/activity-report`
    },

    // Wallet endpoints
    WALLET: {
        BASE: `${API_BASE_URL}/api/wallet`,
        BALANCE: (userId) => `${API_BASE_URL}/api/wallet/${userId}/balance`,
        DEPOSIT: `${API_BASE_URL}/api/wallet/deposit`,
        WITHDRAW: `${API_BASE_URL}/api/wallet/withdraw`,
        EUR_USD_RATE: `${API_BASE_URL}/api/wallet/eur-usd-rate`,
        USDT_ADDRESS: `${API_BASE_URL}/api/wallet/usdt-address`,
        PAYPAL_DEPOSIT_INITIATE: `${API_BASE_URL}/api/wallet/deposit/paypal/initiate`,
        WISE_DEPOSIT_INITIATE: `${API_BASE_URL}/api/wallet/deposit/eur/wise/initiate`,
        EUR_DEPOSIT_INITIATE: `${API_BASE_URL}/api/wallet/deposit/eur/initiate`,
        EUR_DEPOSIT_CONFIG: `${API_BASE_URL}/api/wallet/deposit/eur/config`,
        TRANSACTIONS: (userId) => `${API_BASE_URL}/api/wallet/${userId}/transactions`,
        CAN_WITHDRAW: (userId) => `${API_BASE_URL}/api/wallet/${userId}/can-withdraw`,
        PROMO_PREVIEW: `${API_BASE_URL}/api/wallet/promo-code/preview`
    },

    // Referrals endpoints
    REFERRALS: {
        BASE: `${API_BASE_URL}/api/referrals`,
        EARNINGS_RANGE: `${API_BASE_URL}/api/referrals/earnings-range`,
        TOP_REFERRERS: `${API_BASE_URL}/api/referrals/top-referrers`,
        NETWORK: (userId) => `${API_BASE_URL}/api/referrals/${userId}/network`,
        EARNINGS: (userId) => `${API_BASE_URL}/api/referrals/${userId}/earnings`,
        CALCULATE_BUYIN: `${API_BASE_URL}/api/referrals/calculate/buyin`,
        CALCULATE_MARKETPLACE_FEE: `${API_BASE_URL}/api/referrals/calculate/marketplace-fee`
    },

    // Cards endpoints
    CARDS: {
        BASE: `${API_BASE_URL}/api/cards`,
        GET_BY_ID: (id) => `${API_BASE_URL}/api/cards/${id}`,
        GET_NEXT_MATCH: (id) => `${API_BASE_URL}/api/cards/${id}/next-match`,
        PLAYER_RECENT_SCORES: (playerId) => `${API_BASE_URL}/api/cards/player/${playerId}/recent-scores`,
        NEXT_MATCHES_BATCH: `${API_BASE_URL}/api/cards/next-matches-batch`,
        GET_USER_CARDS: (userId) => `${API_BASE_URL}/api/cards/user/${userId}`,
        SEARCH_PLAYERS: `${API_BASE_URL}/api/cards/players/search`,
        SEARCH_PLAYER_CARDS: `${API_BASE_URL}/api/cards/players/cards`,
        COACH_TRAINING_SESSION: `${API_BASE_URL}/api/cards/coach-training/session`,
        COACH_TRAINING_START: `${API_BASE_URL}/api/cards/coach-training/start`,
        COACH_TRAINING_ACTIVE_CARDS: `${API_BASE_URL}/api/cards/coach-training/active-cards`
    },

    // Tournaments endpoints
    TOURNAMENTS: {
        BASE: `${API_BASE_URL}/api/tournaments`,
        CREATE: `${API_BASE_URL}/api/tournaments`,
        GET_ACTIVE: `${API_BASE_URL}/api/tournaments/active`,
        GET_PUBLISHED_MONTHLY_RANKINGS: `${API_BASE_URL}/api/tournaments/monthly-rankings/published`,
        GET_BY_ID: (id) => `${API_BASE_URL}/api/tournaments/${id}`,
        ENTER: (id) => `${API_BASE_URL}/api/tournaments/${id}/enter`,
        RANKINGS: (id) => `${API_BASE_URL}/api/tournaments/${id}/rankings`,
        GET_USER_REGISTERED: (userId) => `${API_BASE_URL}/api/tournaments/user/${userId}/registered`,
        GET_USED_CARDS_ACTIVE_GAMEWEEKS: (userId) => `${API_BASE_URL}/api/tournaments/user/${userId}/used-cards-active-gameweeks`,
        GET_USED_CARDS_GAMEWEEK: `${API_BASE_URL}/api/tournaments/used-cards/gameweek`,
        CHECK_TEAM_PLAYS_IN_GAMEWEEK: `${API_BASE_URL}/api/tournaments/team-plays-in-gameweek`,
        GET_FORMATION: (id) => `${API_BASE_URL}/api/tournaments/${id}/formation`,
        UPDATE_FORMATION: (id) => `${API_BASE_URL}/api/tournaments/${id}/formation`,
        GET_FORMATION_SCORES: (id) => `${API_BASE_URL}/api/tournaments/${id}/formation/scores`
    },

    // Marketplace endpoints
    MARKETPLACE: {
        BASE: `${API_BASE_URL}/api/marketplace`,
        SETTINGS: `${API_BASE_URL}/api/marketplace/settings`,
        BALANCES: `${API_BASE_URL}/api/marketplace/balances`,
        // Auctions
        CREATE_AUCTION: `${API_BASE_URL}/api/marketplace/auctions`,
        GET_AUCTIONS: `${API_BASE_URL}/api/marketplace/auctions`,
        GET_AUCTIONS_BY_TEMPLATE: (templateId) => `${API_BASE_URL}/api/marketplace/auctions/template/${templateId}`,
        PLACE_BID: (id) => `${API_BASE_URL}/api/marketplace/auctions/${id}/bid`,
        // Sales
        CREATE_SALE: `${API_BASE_URL}/api/marketplace/sales`,
        GET_SALES: `${API_BASE_URL}/api/marketplace/sales`,
        GET_SALES_BY_TEMPLATE: (templateId) => `${API_BASE_URL}/api/marketplace/sales/template/${templateId}`,
        GET_USER_LISTED_SALES: (userId) => `${API_BASE_URL}/api/marketplace/sales/user/${userId}/listed`,
        GET_USER_SOLD_SALES: (userId) => `${API_BASE_URL}/api/marketplace/sales/user/${userId}`,
        CANCEL_SALE: (id) => `${API_BASE_URL}/api/marketplace/sales/${id}/cancel`,
        CANCEL_SALE_BY_CARD: (cardId) => `${API_BASE_URL}/api/marketplace/sales/cancel-by-card/${cardId}`,
        PURCHASE: (id) => `${API_BASE_URL}/api/marketplace/sales/${id}/purchase`,
        // Loans
        GET_LOANS: `${API_BASE_URL}/api/marketplace/loans`,
        GET_LOANS_BY_TEMPLATE: (templateId) => `${API_BASE_URL}/api/marketplace/loans/template/${templateId}`,
        CREATE_LOAN: `${API_BASE_URL}/api/marketplace/loans`,
        ACCEPT_LOAN: (id) => `${API_BASE_URL}/api/marketplace/loans/${id}/accept`,
        GET_USER_LOANS: (userId) => `${API_BASE_URL}/api/marketplace/loans/user/${userId}`,
        GET_USER_LISTED_LOANS: (userId) => `${API_BASE_URL}/api/marketplace/loans/user/${userId}/listed`,
        CANCEL_LOAN: (id) => `${API_BASE_URL}/api/marketplace/loans/${id}/cancel`,
        UPDATE_PERIOD_LOAN_PRICE: (id) => `${API_BASE_URL}/api/marketplace/loans/${id}/period-price`,
        // Trades
        CREATE_TRADE: `${API_BASE_URL}/api/marketplace/trades`,
        GET_TRADE: (id) => `${API_BASE_URL}/api/marketplace/trades/${id}`,
        GET_USER_TRADES: (userId) => `${API_BASE_URL}/api/marketplace/trades/user/${userId}`,
        GET_OPEN_TRADES: `${API_BASE_URL}/api/marketplace/trades/open`,
        GET_OPEN_TRADES_FOR_USER: (userId) => `${API_BASE_URL}/api/marketplace/trades/open/user/${userId}`,
        GET_USER_CREATED_OPEN_TRADES: (userId) => `${API_BASE_URL}/api/marketplace/trades/open/created/${userId}`,
        ACCEPT_TRADE: (id) => `${API_BASE_URL}/api/marketplace/trades/${id}/accept`,
        REJECT_TRADE: (id) => `${API_BASE_URL}/api/marketplace/trades/${id}/reject`,
        CANCEL_TRADE: (id) => `${API_BASE_URL}/api/marketplace/trades/${id}/cancel`,
        // Counter Offers
        CREATE_COUNTER_OFFER: (tradeId) => `${API_BASE_URL}/api/marketplace/trades/${tradeId}/counter-offer`,
        GET_COUNTER_OFFERS_FOR_TRADE: (tradeId) => `${API_BASE_URL}/api/marketplace/trades/${tradeId}/counter-offers`,
        GET_COUNTER_OFFERS_FOR_USER: (userId) => `${API_BASE_URL}/api/marketplace/trades/counter-offers/user/${userId}`,
        ACCEPT_COUNTER_OFFER: (counterOfferId) => `${API_BASE_URL}/api/marketplace/trades/counter-offers/${counterOfferId}/accept`,
        REJECT_COUNTER_OFFER: (counterOfferId) => `${API_BASE_URL}/api/marketplace/trades/counter-offers/${counterOfferId}/reject`,
        CANCEL_COUNTER_OFFER: (counterOfferId) => `${API_BASE_URL}/api/marketplace/trades/counter-offers/${counterOfferId}/cancel`,
        // Packs
        GET_PACKS: `${API_BASE_URL}/api/marketplace/packs`,
        OPEN_PACK: (id) => `${API_BASE_URL}/api/marketplace/packs/${id}/open`,
        OPEN_STARTER_PACK: `${API_BASE_URL}/api/marketplace/packs/starter/open`,
        GET_PACK_HISTORY: (userId) => `${API_BASE_URL}/api/marketplace/packs/user/${userId}`
    },

    // Support endpoints
    SUPPORT: {
        ASK: `${API_BASE_URL}/api/support/ask`,
        ESCALATE: `${API_BASE_URL}/api/support/escalate`,
        USER_TICKETS: (userId) => `${API_BASE_URL}/api/support/user/${userId}`
    },

    // Admin endpoints
    ADMIN: {
        BASE: `${API_BASE_URL}/api/admin`,
        LOGIN: `${API_BASE_URL}/api/admin/auth/login`,
        // Dashboard
        DASHBOARD_KPIS: `${API_BASE_URL}/api/admin/dashboard/kpis`,
        DASHBOARD_USER_GROWTH: `${API_BASE_URL}/api/admin/dashboard/user-growth`,
        DASHBOARD_ECONOMIC_FLOWS: `${API_BASE_URL}/api/admin/dashboard/economic-flows`,
        DASHBOARD_CARD_RARITY_DISTRIBUTION: `${API_BASE_URL}/api/admin/dashboard/card-rarity-distribution`,
        DASHBOARD_GAMEWEEK_TRENDS: `${API_BASE_URL}/api/admin/dashboard/gameweek-trends`,
        // Packs
        PACKS: `${API_BASE_URL}/api/admin/packs`,
        GET_PACK: (id) => `${API_BASE_URL}/api/admin/packs/${id}`,
        PACKS_STATS: `${API_BASE_URL}/api/admin/packs/stats`,
        // Users
        USERS: `${API_BASE_URL}/api/admin/users`,
        GET_USER: (id) => `${API_BASE_URL}/api/admin/users/${id}`,
        BAN_USER: (id) => `${API_BASE_URL}/api/admin/users/${id}/ban`,
        UNBAN_USER: (id) => `${API_BASE_URL}/api/admin/users/${id}/unban`,
        ADJUST_BALANCE: (id) => `${API_BASE_URL}/api/admin/users/${id}/balance-adjust`,
        PAYPAL_DEPOSITS: `${API_BASE_URL}/api/admin/paypal-deposits`,
        CONFIRM_PAYPAL_DEPOSIT: (id) => `${API_BASE_URL}/api/admin/paypal-deposits/${id}/confirm`,
        PROMO_CODE: `${API_BASE_URL}/api/admin/promo-code`,
        PROMO_CODE_GRANT_BONUS: `${API_BASE_URL}/api/admin/promo-code/grant-bonus`,
        USER_LOGIN_HISTORY: (id) => `${API_BASE_URL}/api/admin/users/${id}/login-history`,
        ALL_LOGIN_HISTORY: `${API_BASE_URL}/api/admin/login-history`,
        SECURITY_REPORTS: `${API_BASE_URL}/api/admin/security-reports`,
        GET_SECURITY_REPORT: (id) => `${API_BASE_URL}/api/admin/security-reports/${id}`,
        UPDATE_SECURITY_REPORT_STATUS: (id) => `${API_BASE_URL}/api/admin/security-reports/${id}/update-status`,
        SUPPORT_TICKETS: `${API_BASE_URL}/api/admin/support/tickets`,
        SUPPORT_TICKET_STATUS: (id) => `${API_BASE_URL}/api/admin/support/tickets/${id}/status`,
        ANNOUNCEMENTS: `${API_BASE_URL}/api/admin/announcements`,
        CREATE_ANNOUNCEMENT: `${API_BASE_URL}/api/admin/announcements`,
        UPDATE_ANNOUNCEMENT: (id) => `${API_BASE_URL}/api/admin/announcements/${id}`,
        DELETE_ANNOUNCEMENT: (id) => `${API_BASE_URL}/api/admin/announcements/${id}`,
        MAINTENANCE_ALERTS: `${API_BASE_URL}/api/admin/maintenance-alerts`,
        CREATE_MAINTENANCE_ALERT: `${API_BASE_URL}/api/admin/maintenance-alerts`,
        UPDATE_MAINTENANCE_ALERT_STATUS: (id) => `${API_BASE_URL}/api/admin/maintenance-alerts/${id}/status`,
        DELETE_MAINTENANCE_ALERT: (id) => `${API_BASE_URL}/api/admin/maintenance-alerts/${id}`,
        CREATE_NOTIFICATION: `${API_BASE_URL}/api/admin/notifications`,
        PLATFORM_EMAIL_STATUS: `${API_BASE_URL}/api/admin/platform-email/status`,
        PLATFORM_EMAIL_SEND: `${API_BASE_URL}/api/admin/platform-email/send`,
        // Admins
        ADMINS: `${API_BASE_URL}/api/admin/admins`,
        CREATE_ADMIN: `${API_BASE_URL}/api/admin/admins`,
        PROMOTE_USER: `${API_BASE_URL}/api/admin/admins/promote-user`,
        UPDATE_ADMIN_ROLE: (id) => `${API_BASE_URL}/api/admin/admins/${id}/role`,
        DEACTIVATE_ADMIN: (id) => `${API_BASE_URL}/api/admin/admins/${id}/deactivate`,
        // GameWeeks
        GAMEWEEKS: `${API_BASE_URL}/api/admin/gameweeks`,
        GET_ALL_GAMEWEEKS: `${API_BASE_URL}/api/admin/gameweeks`,
        GET_GAMEWEEK: (id) => `${API_BASE_URL}/api/admin/gameweeks/${id}`,
        CREATE_GAMEWEEK: `${API_BASE_URL}/api/admin/gameweeks`,
        DELETE_GAMEWEEK: (id) => `${API_BASE_URL}/api/admin/gameweeks/${id}`,
        DELETE_ALL_GAMEWEEKS: `${API_BASE_URL}/api/admin/gameweeks/reset-all`,
        GAMEWEEK_PRIZE_BREAKDOWN: (id) => `${API_BASE_URL}/api/admin/gameweeks/${id}/prize-breakdown`,
        GAMEWEEK_TOURNAMENTS: (id) => `${API_BASE_URL}/api/admin/gameweeks/${id}/tournaments`,
        GAMEWEEK_TEAM_CARDS_XP: (id) => `${API_BASE_URL}/api/admin/gameweeks/${id}/team-cards-xp`,
        GAMEWEEK_FIX_XP_ZERO: (id) => `${API_BASE_URL}/api/admin/gameweeks/${id}/fix-xp-zero`,
        TOURNAMENT_DETAILS: (id) => `${API_BASE_URL}/api/admin/tournaments/${id}/details`,
        UPDATE_GAMEWEEK_STATUS: (id) => `${API_BASE_URL}/api/admin/gameweeks/${id}/status`,
        GAMEWEEK_PATCH_SCHEDULE: (id) => `${API_BASE_URL}/api/admin/gameweeks/${id}/schedule`,
        GAMEWEEK_HIDE_PRIZE: (id) => `${API_BASE_URL}/api/admin/gameweeks/${id}/hide-prize`,
        GAMEWEEK_PRIZES: (id) => `${API_BASE_URL}/api/admin/gameweeks/${id}/prizes`,
        GAMEWEEK_RECALC_PRIZE_POOLS: (id) => `${API_BASE_URL}/api/admin/gameweeks/${id}/recalculate-prize-pools`,
        GAMEWEEK_PAY_PRIZES: (id) => `${API_BASE_URL}/api/admin/gameweeks/${id}/pay-prizes`,
        GAMEWEEK_RECALC_SCORES: (id) => `${API_BASE_URL}/api/admin/gameweeks/${id}/recalc-scores`,
        GAMEWEEK_RECALC_SCORES_STATUS: (id) => `${API_BASE_URL}/api/admin/gameweeks/${id}/recalc-scores/status`,
        GAMEWEEK_SYNC_MATCH_STATS: (id) => `${API_BASE_URL}/api/admin/gameweeks/${id}/sync-match-stats`,
        GAMEWEEK_SYNC_MATCH_STATS_STATUS: (id) => `${API_BASE_URL}/api/admin/gameweeks/${id}/sync-match-stats/status`,
        GAMEWEEK_REFRESH_TOOLTIP_SCORE: (id) => `${API_BASE_URL}/api/admin/gameweeks/${id}/refresh-tooltip-score`,
        GAMEWEEK_REFRESH_TOOLTIP_SCORE_STATUS: (id) => `${API_BASE_URL}/api/admin/gameweeks/${id}/refresh-tooltip-score/status`,
        GAMEWEEK_SCHEDULE: `${API_BASE_URL}/api/admin/gameweeks/schedule`,
        GAMEWEEK_GENERATE: `${API_BASE_URL}/api/admin/gameweeks/generate`,
        GAMEWEEK_GENERATE_MULTIPLE: `${API_BASE_URL}/api/admin/gameweeks/generate-multiple`,
        GAMEWEEK_ACTIVE: `${API_BASE_URL}/api/admin/gameweeks/active`,
        GAMEWEEK_UPDATE_STATUSES: `${API_BASE_URL}/api/admin/gameweeks/update-statuses`,
        FINALIZE_AUCTION_REVENUE: `${API_BASE_URL}/api/admin/marketplace/auctions/finalize-expired`,
        XP_FIX_ZERO_CARDS: `${API_BASE_URL}/api/admin/xp/fix-zero-cards`,
        XP_FIX_ZERO_CARDS_STATUS: `${API_BASE_URL}/api/admin/xp/fix-zero-cards/status`,
        // Scores
        SCORES_RAW: (gameweekId) => `${API_BASE_URL}/api/admin/scores/raw/${gameweekId}`,
        SCORES_FINAL: (playerScoreId) => `${API_BASE_URL}/api/admin/scores/final/${playerScoreId}`,
        SCORES_OVERRIDE: `${API_BASE_URL}/api/admin/scores/override`,
        SCORES_OVERRIDE_HISTORY: `${API_BASE_URL}/api/admin/scores/override-history`,
        // Prizes
        PRIZES_CALCULATE: (tournamentId) => `${API_BASE_URL}/api/admin/prizes/calculate/${tournamentId}`,
        PRIZES_PAY: (tournamentId) => `${API_BASE_URL}/api/admin/prizes/pay/${tournamentId}`,
        PRIZES_HISTORY: `${API_BASE_URL}/api/admin/prizes/history`,
        PRIZES_ALLOCATE: `${API_BASE_URL}/api/admin/prizes/allocate`,
        // Treasury
        TREASURY_FUNDS: `${API_BASE_URL}/api/admin/treasury/funds`,
        TREASURY_TRANSFER: `${API_BASE_URL}/api/admin/treasury/transfer`,
        TREASURY_ADD: `${API_BASE_URL}/api/admin/treasury/add`,
        TREASURY_HISTORY: `${API_BASE_URL}/api/admin/treasury/history`,
        PRIZE_POOLS_SUMMARY: `${API_BASE_URL}/api/admin/prize-pools/summary`,
        RANKING_PERIODS: `${API_BASE_URL}/api/admin/rankings/periods`,
        RANKING_MONTHLY_FIRST: `${API_BASE_URL}/api/admin/rankings/monthly/first`,
        RANKING_MONTHLY_BY_PERIOD: (periodKey) => `${API_BASE_URL}/api/admin/rankings/monthly/${periodKey}`,
        RANKING_MONTHLY_PUBLISH: (periodKey) => `${API_BASE_URL}/api/admin/rankings/monthly/${periodKey}/publish`,
        RANKING_MONTHLY_PAYOUT_PREVIEW: (periodKey, category) => `${API_BASE_URL}/api/admin/rankings/monthly/${periodKey}/${category}/payout-preview`,
        RANKING_MONTHLY_PAY: (periodKey, category) => `${API_BASE_URL}/api/admin/rankings/monthly/${periodKey}/${category}/pay`,
        RANKING_CLOSE: `${API_BASE_URL}/api/admin/rankings/close`,
        // Mondiali
        MONDIALI_NEXT_MATCH_SYNC: `${API_BASE_URL}/api/admin/mondiali/next-match-sync`,
        MONDIALI_SYNC_NEXT_MATCH: `${API_BASE_URL}/api/admin/mondiali/sync-next-match`,
        MONDIALI_SYNC_NEXT_MATCH_STATUS: `${API_BASE_URL}/api/admin/mondiali/sync-next-match/status`,
        MONDIALI_CONVOCATED: `${API_BASE_URL}/api/admin/mondiali/convocated`,
        MONDIALI_CONVOCATED_REFRESH: `${API_BASE_URL}/api/admin/mondiali/convocated/refresh`,
        MONDIALI_CONVOCATED_STATUS: `${API_BASE_URL}/api/admin/mondiali/convocated/status`,
        SECONDARY_MARKET_SETTINGS: `${API_BASE_URL}/api/admin/game-management/secondary-market`,
        // Settings (auction default prices)
        SETTINGS_AUCTION_DEFAULT_PRICES: `${API_BASE_URL}/api/admin/settings/auction-default-prices`,
        // Settings > Players (teams, players by team, sync league/team)
        SETTINGS_PLAYERS_TEAMS: `${API_BASE_URL}/api/admin/settings/players/teams`,
        SETTINGS_PLAYERS_TEAM_PLAYERS: (teamId) => `${API_BASE_URL}/api/admin/settings/players/teams/${teamId}/players`,
        SETTINGS_PLAYERS_SYNC_CHANGES: `${API_BASE_URL}/api/admin/settings/players/sync-changes`,
        SETTINGS_PLAYERS_UPDATE: (playerId) => `${API_BASE_URL}/api/admin/settings/players/${playerId}`,
        PLAYERS_SYNC_LEAGUE_TEAM: `${API_BASE_URL}/api/admin/players/sync-league-team`,
        PLAYERS_SYNC_LEAGUE_TEAM_STATUS: `${API_BASE_URL}/api/admin/players/sync-league-team/status`,
        // Logs
        LOGS: `${API_BASE_URL}/api/admin/logs`,
        GET_LOG: (id) => `${API_BASE_URL}/api/admin/logs/${id}`,
        LOGS_STATISTICS: `${API_BASE_URL}/api/admin/logs/statistics`,
        // Marketplace
        MARKETPLACE_STATS: `${API_BASE_URL}/api/admin/marketplace/stats`,
        MARKETPLACE_ACTIVITY: `${API_BASE_URL}/api/admin/marketplace/activity`,
        MARKETPLACE_CANCEL_AUCTION: (id) => `${API_BASE_URL}/api/admin/marketplace/auctions/${id}/cancel`,
        MARKETPLACE_FINALIZE_EXPIRED_AUCTIONS: `${API_BASE_URL}/api/admin/marketplace/auctions/finalize-expired`,
        MARKETPLACE_BLOCK_CARD: (id) => `${API_BASE_URL}/api/admin/marketplace/cards/${id}/block`,
        MARKETPLACE_UNBLOCK_CARD: (id) => `${API_BASE_URL}/api/admin/marketplace/cards/${id}/unblock`,
        MARKETPLACE_REFUNDS: `${API_BASE_URL}/api/admin/marketplace/refunds`,
        MARKETPLACE_ALERTS: `${API_BASE_URL}/api/admin/marketplace/alerts`,
        MARKETPLACE_CREATE_ALERT: `${API_BASE_URL}/api/admin/marketplace/alerts`,
        MARKETPLACE_RESOLVE_ALERT: (id) => `${API_BASE_URL}/api/admin/marketplace/alerts/${id}/resolve`,
        MARKETPLACE_DISMISS_ALERT: (id) => `${API_BASE_URL}/api/admin/marketplace/alerts/${id}/dismiss`,
        MARKETPLACE_COMPANY_CARDS: `${API_BASE_URL}/api/admin/marketplace/company-cards`,
        MARKETPLACE_COMPANY_CARDS_FILTER_OPTIONS: `${API_BASE_URL}/api/admin/marketplace/company-cards/filter-options`,
        MARKETPLACE_COMPANY_CARDS_DELETE: `${API_BASE_URL}/api/admin/marketplace/company-cards/delete`,
        MARKETPLACE_COMPANY_ACTIVE_LISTINGS: `${API_BASE_URL}/api/admin/marketplace/company-active-listings`,
        MARKETPLACE_CONCLUDED_AUCTIONS: `${API_BASE_URL}/api/admin/marketplace/concluded-auctions`,
        MARKETPLACE_CONCLUDED_SALES: `${API_BASE_URL}/api/admin/marketplace/concluded-sales`,
        MARKETPLACE_COMPANY_SALES: `${API_BASE_URL}/api/admin/marketplace/company-sales`,
        MARKETPLACE_COMPANY_AUCTIONS: `${API_BASE_URL}/api/admin/marketplace/company-auctions`,
        MARKETPLACE_COMPANY_AUCTIONS_PREVIEW_TEAM: `${API_BASE_URL}/api/admin/marketplace/company-auctions/preview-team`,
        MARKETPLACE_COMPANY_AUCTIONS_TEAM_BATCH: `${API_BASE_URL}/api/admin/marketplace/company-auctions/team-batch`,
        MARKETPLACE_COMPANY_LOANS: `${API_BASE_URL}/api/admin/marketplace/company-loans`,
        MARKETPLACE_UPDATE_SALE_PRICE: (id) => `${API_BASE_URL}/api/admin/marketplace/sales/${id}/price`,
        MARKETPLACE_REMOVE_SALE: (id) => `${API_BASE_URL}/api/admin/marketplace/sales/${id}`,
        MARKETPLACE_UPDATE_AUCTION_END_TIME: (id) => `${API_BASE_URL}/api/admin/marketplace/auctions/${id}/end-time`,
        MARKETPLACE_REMOVE_AUCTION: (id) => `${API_BASE_URL}/api/admin/marketplace/auctions/${id}`,
        MARKETPLACE_UPDATE_LOAN: (id) => `${API_BASE_URL}/api/admin/marketplace/loans/${id}`,
        MARKETPLACE_REMOVE_LOAN: (id) => `${API_BASE_URL}/api/admin/marketplace/loans/${id}`,
        // Cards
        CARDS_GET_PLAYERS: `${API_BASE_URL}/api/admin/cards/players`,
        CARDS_GET_RARITIES: `${API_BASE_URL}/api/admin/cards/rarities`,
        CARDS_GET_REMAINING: `${API_BASE_URL}/api/admin/cards/remaining`,
        CARDS_GET_LEAGUES: `${API_BASE_URL}/api/admin/cards/leagues`,
        CARDS_GET_TEAMS: `${API_BASE_URL}/api/admin/cards/teams`,
        CARDS_CREATE: `${API_BASE_URL}/api/admin/cards`,
        CARDS_CREATE_BULK: `${API_BASE_URL}/api/admin/cards/bulk`,
        COACH_CARDS: `${API_BASE_URL}/api/admin/coach-cards`,
        COACH_CARDS_ASSIGN_OWNER: (id) => `${API_BASE_URL}/api/admin/coach-cards/${id}/assign-owner`,
        // Withdrawals
        WITHDRAWALS: `${API_BASE_URL}/api/admin/withdrawals`,
        GET_WITHDRAWAL: (id) => `${API_BASE_URL}/api/admin/withdrawals/${id}`,
        WITHDRAWALS_STATS: `${API_BASE_URL}/api/admin/withdrawals/stats`,
        APPROVE_WITHDRAWAL: (id) => `${API_BASE_URL}/api/admin/withdrawals/${id}/approve`,
        REJECT_WITHDRAWAL: (id) => `${API_BASE_URL}/api/admin/withdrawals/${id}/reject`,
        PAY_WITHDRAWAL_CRYPTO: (id) => `${API_BASE_URL}/api/admin/withdrawals/${id}/pay-crypto`,
        PAY_WITHDRAWAL_CRYPTO_STATUS: (id) => `${API_BASE_URL}/api/admin/withdrawals/${id}/pay-crypto/status`,
        // Affiliations
        AFFILIATIONS_STATS: `${API_BASE_URL}/api/admin/affiliations/stats`,
        AFFILIATIONS_NETWORK: (userId) => `${API_BASE_URL}/api/admin/affiliations/network/${userId}`,
        AFFILIATIONS_REFERRALS: `${API_BASE_URL}/api/admin/affiliations/referrals`,
        AFFILIATIONS_REFERRAL_STATUS: (id) => `${API_BASE_URL}/api/admin/affiliations/referrals/${id}/status`,
        AFFILIATIONS_REFERRAL_CHANGE_REFERRER: (id) => `${API_BASE_URL}/api/admin/affiliations/referrals/${id}/referrer`,
        AFFILIATIONS_EARNINGS: `${API_BASE_URL}/api/admin/affiliations/earnings`,
        AFFILIATIONS_EARNINGS_UPDATE: (id) => `${API_BASE_URL}/api/admin/affiliations/earnings/${id}`,
        AFFILIATIONS_EARNINGS_DELETE: (id) => `${API_BASE_URL}/api/admin/affiliations/earnings/${id}`,
        AFFILIATIONS_EARNINGS_CREATE: `${API_BASE_URL}/api/admin/affiliations/earnings`,
        AFFILIATIONS_USER_BLOCK: (id) => `${API_BASE_URL}/api/admin/affiliations/user/${id}/block`,
        AFFILIATIONS_USER_UNBLOCK: (id) => `${API_BASE_URL}/api/admin/affiliations/user/${id}/unblock`,
        AFFILIATIONS_TOP_REFERRERS: `${API_BASE_URL}/api/admin/affiliations/top-referrers`
    },

    // Health check
    HEALTH: `${API_BASE_URL}/health`,
    API_INFO: `${API_BASE_URL}/api`,

    // Game assets (music served from backend)
    GAME_MUSIC: `${API_BASE_URL}/assets/music/gamemusic.mp3`,
    GAME_MUSIC_2: `${API_BASE_URL}/assets/music/gamemusic2.mp3`
};

// HTTP methods
export const HTTP_METHODS = {
    GET: 'GET',
    POST: 'POST',
    PUT: 'PUT',
    DELETE: 'DELETE',
    PATCH: 'PATCH'
};

// Request timeout (in milliseconds)
export const REQUEST_TIMEOUT = 30000; // 60 seconds

// Default headers for all requests
export const DEFAULT_HEADERS = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
};

// API response status codes
export const STATUS_CODES = {
    OK: 200,
    CREATED: 201,
    NO_CONTENT: 204,
    BAD_REQUEST: 400,
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    CONFLICT: 409,
    UNPROCESSABLE_ENTITY: 422,
    TOO_MANY_REQUESTS: 429,
    INTERNAL_SERVER_ERROR: 500,
    SERVICE_UNAVAILABLE: 503
};

// Error messages
export const ERROR_MESSAGES = {
    NETWORK_ERROR: 'Network error. Please check your connection.',
    TIMEOUT_ERROR: 'Request timeout. Please try again.',
    UNAUTHORIZED: 'You are not authorized. Please login again.',
    SERVER_ERROR: 'Server error. Please try again later.',
    VALIDATION_ERROR: 'Please check your input and try again.',
    NOT_FOUND: 'The requested resource was not found.',
    TOO_MANY_REQUESTS: 'Too many requests. Please wait before trying again.'
};

// Export API_BASE_URL for direct use
export { API_BASE_URL };

const config = {
    API_BASE_URL,
    API_ENDPOINTS,
    HTTP_METHODS,
    REQUEST_TIMEOUT,
    DEFAULT_HEADERS,
    STATUS_CODES,
    ERROR_MESSAGES
};

export default config;
